import { combineReducers } from '@reduxjs/toolkit';
import { layoutsList, savedLayoutsList } from '../utils/artworkServices';
import { call, takeLatest, put, select } from 'redux-saga/effects';
import PicabooConstants from '../common/PicabooConstants';
import * as Selectors from '../reducers/selectors';
import SpineHelper from '../helpers/SpineHelper';
import {
  deleteDesignerLayout,
  saveDesignerLayout,
} from '../utils/zoomServices';
import { showProcessing } from './modals';
import { pageToLayout } from '../utils/pageToLayout';
import invariant from 'tiny-invariant';

const SET_CURRENT_LAYOUTS = 'SET_CURRENT_LAYOUTS';
const GET_CURRENT_LAYOUTS = 'GET_CURRENT_LAYOUTS';
const SET_LAYOUT_CATEGORY = 'SET_LAYOUT_CATEGORY';
const SET_SAVED_LAYOUTS = 'SET_SAVED_LAYOUTS';
const GET_SAVED_LAYOUTS = 'GET_SAVED_LAYOUTS';
export const SAVE_LAYOUT = 'layouts/SAVE_LAYOUT';
export const DELETE_LAYOUT = 'layouts/DELETE_LAYOUT';
export const LAYOUT_MIRROR_TOGGLE = 'layouts/MIRROR_TOGGLE_LAYOUT';

export const APPLY_LAYOUT_TO_PAGES = 'APPLY_LAYOUT_TO_PAGES';
export const APPLY_PAGELAYOUT_TO_PAGES = 'APPLY_PAGELAYOUT_TO_PAGES';
export const APPLY_PAGEDESIGN_TO_PAGE = 'APPLY_PAGEDESIGN_TO_PAGE';

export const SAVE_LAYOUT_TYPE_PERSONAL = 'SAVE_LAYOUT_TYPE_PERSONAL';
export const SAVE_LAYOUT_TYPE_DESIGNER = 'SAVE_LAYOUT_TYPE_DESIGNER';

export const ADJUST_PAGE_LAYOUT = 'ADJUST_PAGE_LAYOUT';

export const SUPPORTS_LAYOUTS = [
  PicabooConstants.PRODUCT_TYPES.BOOK,
  PicabooConstants.PRODUCT_TYPES.CALENDAR,
  PicabooConstants.PRODUCT_TYPES.CANVAS,
  PicabooConstants.PRODUCT_TYPES.COLLAGE,
  PicabooConstants.PRODUCT_TYPES.PERSONALIZED_YEARBOOK,
  PicabooConstants.PRODUCT_TYPES.PHOTO_PANEL,
  PicabooConstants.PRODUCT_TYPES.SQUARE_PRINT,
  PicabooConstants.PRODUCT_TYPES.YEARBOOK,
  PicabooConstants.PRODUCT_TYPES.BLANKET,
  PicabooConstants.PRODUCT_TYPES.PILLOW,
  PicabooConstants.PRODUCT_TYPES.TOTE,
  PicabooConstants.PRODUCT_TYPES.MUG,
  PicabooConstants.PRODUCT_TYPES.PUZZLE,
  PicabooConstants.PRODUCT_TYPES.PHOTO_POSTER,
  PicabooConstants.PRODUCT_TYPES.FRAME,
];

/**
 *  reducers
 */
function layouts(state = [], action) {
  switch (action.type) {
    case SET_CURRENT_LAYOUTS: {
      let newState = action.layouts || state;
      return newState;
    }
  }

  return state;
}

function savedLayouts(
  state = {
    Layouts: [],
    PortraitLayouts: [],
    TeamLayouts: [],
    TeamPortraitLayouts: [],
  },
  action,
) {
  switch (action.type) {
    case SET_SAVED_LAYOUTS:
      let layouts = [];
      let portraitLayouts = [];
      let teamLayouts = [];
      let teamPortraitLayouts = [];
      for (let layout of action.savedLayouts.Layouts) {
        if (layout) {
          if (layout.applyTo === 'P') {
            layouts.push(layout);
          } else if (layout.applyTo === 'Y') {
            portraitLayouts.push(layout);
          } else if (layout.applyTo === 'W') {
            layouts.push(layout);
          }
        }
      }

      if (action.savedLayouts.TeamLayouts) {
        for (let layout of action.savedLayouts.TeamLayouts) {
          if (layout) {
            if (layout.applyTo === 'P') {
              teamLayouts.push(layout);
            } else if (layout.applyTo === 'Y') {
              teamPortraitLayouts.push(layout);
            }
          }
        }
      }

      return {
        Layouts: layouts,
        PortraitLayouts: portraitLayouts,
        TeamLayouts: teamLayouts,
        TeamPortraitLayouts: teamPortraitLayouts,
      };
  }

  return state;
}

const layoutsReducer = combineReducers({
  layouts,
  savedLayouts,
});

// export default layoutsReducer

/**
 *  actions
 */
export function setCurrentLayouts(layouts) {
  return {
    type: SET_CURRENT_LAYOUTS,
    layouts,
  };
}

export function getCurrentLayouts(productId) {
  return {
    type: GET_CURRENT_LAYOUTS,
    productId,
  };
}

export function setLayoutCategory(categoryIndex) {
  return {
    type: SET_LAYOUT_CATEGORY,
    categoryIndex,
  };
}

export function setSavedLayouts(savedLayouts) {
  return {
    type: SET_SAVED_LAYOUTS,
    savedLayouts,
  };
}

export function getSavedLayouts() {
  return {
    type: GET_SAVED_LAYOUTS,
  };
}

export function saveLayout(
  page,
  name,
  saveType,
  adjustToSpine = false,
  update = false,
) {
  return {
    type: SAVE_LAYOUT,
    page,
    name,
    saveType,
    adjustToSpine,
    update,
  };
}

export function deleteLayout(layoutID, isSavedLayout = false) {
  return {
    type: DELETE_LAYOUT,
    layoutID,
    isSavedLayout,
  };
}

export function toggleMirror(layout) {
  return {
    type: LAYOUT_MIRROR_TOGGLE,
    layout,
  };
}

/**
 *  actions
 */
export function applyLayoutToPages(
  layout,
  pages,
  saveCandids = false,
  gridData = null,
) {
  return {
    type: APPLY_LAYOUT_TO_PAGES,
    changes: {
      layout,
      pages,
      keepExistingItems: saveCandids,
      gridData,
    },
  };
}

export function applyPageLayoutToPages(layout, pages) {
  const pageID = layout.pageID;
  const projectPK = layout.projectPK;
  const srcProjectPK = layout.srcProjectPK;
  return {
    type: APPLY_PAGELAYOUT_TO_PAGES,
    changes: {
      pageID,
      projectPK,
      pages,
      srcProjectPK,
    },
  };
}

export function applyPageDesignToPages(srcPageID, srcProjectPK, pageID) {
  return {
    type: APPLY_PAGEDESIGN_TO_PAGE,
    changes: {
      srcPageID,
      srcProjectPK,
      pageID,
    },
  };
}

export function adjustPageLayout(pageID) {
  return {
    type: ADJUST_PAGE_LAYOUT,
    pageID,
  };
}

//layout helper
export function getLayoutOptions(productId) {
  const singlePage = { value: 'singlePage', text: 'Single Page' };
  switch (productId) {
    case PicabooConstants.PRODUCT_TYPES.FRAME:
    case PicabooConstants.PRODUCT_TYPES.COLLAGE:
    case PicabooConstants.PRODUCT_TYPES.CANVAS:
    case PicabooConstants.PRODUCT_TYPES.PHOTO_PANEL:
    case PicabooConstants.PRODUCT_TYPES.METAL_PRINT:
    case PicabooConstants.PRODUCT_TYPES.MOUSE_PAD:
    case PicabooConstants.PRODUCT_TYPES.CALENDAR:
      return [];
    default:
      return [singlePage, { value: 'twoPage', text: 'Two Page' }];
  }
}

/**
 * Sagas
 */

function* getLayouts(action) {
  try {
    const productID = yield select(Selectors.projectProductIDSelector);
    const layouts = yield layoutsList(action.productId || productID);
    if (layouts) {
      yield put(setCurrentLayouts(layouts));
    }
  } catch (error) {
    console.error('Error retrieving layouts: ' + error);
  }
}

function* getSavedLayoutsSaga() {
  try {
    const productID = yield select(Selectors.projectProductIDSelector);
    const layouts = yield savedLayoutsList(productID);
    yield put(setSavedLayouts(layouts));
  } catch (error) {
    console.error('Error getting saved Layouts' + error);
  }
}

function* saveLayoutSaga(action) {
  invariant(action.saveType === SAVE_LAYOUT_TYPE_DESIGNER);
  try {
    const name = action.name;
    const page = action.page;

    const pageItemObjects = yield select(Selectors.pageItemsSelector);
    const productID = yield select(Selectors.projectProductIDSelector);
    const project = yield select(Selectors.projectMetadataSelector);
    const type = project.get('type');

    const layoutID = action.update ? page.currentLayout : undefined;
    const includeMirroredVersion =
      type === PicabooConstants.PRODUCT_TYPES.YEARBOOK &&
      page.pageType === PicabooConstants.PAGE_TYPES.NORMAL &&
      page.ratioBase != 2;
    const layout = pageToLayout({
      page,
      name,
      pageItemObjects,
      includeMirroredVersion,
    });

    if (layout.applyTo === 'W') {
      let selectedSize = yield select(Selectors.selectedSize);
      let projectMetadata = yield select(Selectors.projectMetadataSelector);
      let editorDim = yield select(Selectors.editorDimSelector);
      let wrap = yield select(Selectors.wrapSelector);

      let spineWidth = SpineHelper.calculateSpineWidth(
        editorDim.width,
        projectMetadata.get('numPages'),
        null,
        selectedSize.get('sku') || projectMetadata.get('style'),
      );

      layout.spinePercentage = spineWidth / editorDim.width;
      layout.verticalWrap = wrap.get('horizontalWrap');
      layout.horizontalWrap = wrap.get('verticalWrap');
      layout.adjustToSpine = action.adjustToSpine;
    }

    let result = yield call(
      saveDesignerLayout,
      type,
      productID,
      layout,
      layoutID,
    );
    if (result.data.success) {
      alert(
        'The layout has been saved.  The saved layout id is:' +
          result.data.layoutID,
      );
    } else {
      alert('Error saving the layout');
    }
    yield put(getCurrentLayouts());
  } catch (error) {
    console.error('Error saving Layout' + error);
  }
}

function* toggleLayoutSaga(action) {
  let layout = action.layout;
  layout.includeMirroredVersion =
    layout.includeMirroredVersion === 'false'
      ? true
      : !layout.includeMirroredVersion;

  const project = yield select(Selectors.projectMetadataSelector);
  const type = project.get('type');
  let productID = project.getIn(['product', 'id']);
  yield call(saveDesignerLayout, type, productID, layout, layout.id);
  yield put(getCurrentLayouts());
}

function* deleteLayoutSaga(action) {
  const project = yield select(Selectors.projectMetadataSelector);
  const layoutID = action.layoutID;
  const isSavedLayout = action.isSavedLayout;
  invariant(!isSavedLayout);
  const isDesigner = yield select(Selectors.isDesigner);
  invariant(isDesigner);

  yield call(deleteDesignerLayout, layoutID);

  yield call(
    alert,
    'The layout has been deleted. Enjoy the sound of our customers weeping...',
  );
  yield put(getCurrentLayouts());
}

function* adjustPageLayoutSaga(action) {
  yield put(showProcessing());

  let pages = yield select(Selectors.pagesSelector);
  let page = pages.get(action.pageID);
  let isBook = yield select(Selectors.isBook);
  let isSpread = isBook && pages.count() === 1;
  let numPages = isSpread ? 2 : 1;

  //count the number of graphic items (non-empty) and find a layout
  let backgroundID = page.get('background').get('graphicItem');
  let pageItems = yield select(Selectors.pageItemsSelector);
  let graphicItemCount = pageItems.count((pi) => {
    return (
      pi.get('pageID') === action.pageID &&
      pi.get('itemType') === 'graphicItem' &&
      pi.get('id') !== backgroundID &&
      pi.get('imageID')
    );
  });

  let pageType = page.get('pageType');
  let layoutType = pageType;
  if (pageType === 'C') {
    layoutType = 'W';
    numPages = 1;
  }

  let layouts = yield select(Selectors.layoutSelector);
  layouts = layouts.layouts;
  //Group the layouts by well count
  let arrays = {};
  let exactMatchFound = false;
  let maxWells = 0;
  for (let id in layouts) {
    let layout = layouts[id];
    if (layout.wells === '' || layout.wells === '0') {
      continue;
    }

    if (
      layout.applyTo === layoutType &&
      (layout.numPages === undefined || layout.numPages == numPages)
    ) {
      maxWells = Math.max(maxWells, layout.wells);
      let key = '' + layout.wells;
      if (!arrays[key]) {
        arrays[key] = [];
      }
      arrays[key].push(layout);

      exactMatchFound = exactMatchFound || layout.wells == graphicItemCount;
    }
  }

  //Find a layout with the smallest number of wells that contains at least as many wells as our page
  let layoutList = null;
  if (exactMatchFound) {
    layoutList = arrays['' + graphicItemCount];
  } else if (graphicItemCount > maxWells) {
    layoutList = arrays['' + maxWells];
  } else {
    let wellCount = 0;
    for (let i = 1; i <= maxWells; i++) {
      if (!arrays['' + i]) {
        continue;
      }

      if (i > graphicItemCount) {
        wellCount = i;
        break;
      }
    }

    layoutList = arrays['' + wellCount];
  }

  if (layoutList) {
    let layout = layoutList[Math.floor(Math.random() * layoutList.length)];
    yield put(applyLayoutToPages(layout, [action.pageID]));
  }
}

function* setupSagas() {
  yield takeLatest(GET_CURRENT_LAYOUTS, getLayouts);
  yield takeLatest(GET_SAVED_LAYOUTS, getSavedLayoutsSaga);
  yield takeLatest(SAVE_LAYOUT, saveLayoutSaga);
  yield takeLatest(DELETE_LAYOUT, deleteLayoutSaga);
  yield takeLatest(ADJUST_PAGE_LAYOUT, adjustPageLayoutSaga);
  yield takeLatest(LAYOUT_MIRROR_TOGGLE, toggleLayoutSaga);
}

/** ***********************************************************************
 *  Main Export
 *  *********************************************************************** */
export default {
  reducer: layoutsReducer,
  saga: setupSagas,
};
